import { useEffect, useRef } from 'react';
import {
  useFieldArray,
  type Control,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, PackageOpen, Plus, QrCode, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { Input } from '@/shared/ui/inputs/Input';
import { NumberInput } from '@/shared/ui/inputs/NumberInput';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Select } from '@/shared/ui/selects/Select';
import { cn } from '@/shared/utils/cn';
import { downloadQr } from '@/shared/utils/qr';
import { normalizeDecimal } from '@/shared/utils/number';
import { formatUsd, roundMoney } from '@/shared/utils/currency';
import { apiPackagesNextName } from '@/entities/packages';
import { isCountedInLimit, usePinflLimit } from '@/entities/pinfl';
import { REGION_VALUES } from '@/shared/utils/regions';
import { WEIGHT_UNIT_VALUES } from '@/shared/types/api/packages';
import {
  allocQtyInvalid,
  type FormValues,
  type ProductForm,
} from '@/widgets/split-distribute/modules/ui/model';

type Props = {
  index: number;
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  watch: UseFormWatch<FormValues>;
  products: ProductForm[];
  /** Source parcel id: the limit baseline excludes it (live allocations are added). */
  parcelId: string;
  /** Product ids whose total allocation (across all PINFLs) is misallocated. */
  productOverMap: Map<string, boolean>;
  /** USD price per kg, used to compute the per-package price for display. */
  pricePerKg: number;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  disabled: boolean;
  readOnly: boolean;
  /** True after a Save attempt — enables per-field red borders. */
  submitAttempted?: boolean;
};

export const PinflCard = ({
  index,
  control,
  register,
  setValue,
  watch,
  products,
  parcelId,
  productOverMap,
  pricePerKg,
  expanded,
  onToggle,
  onRemove,
  disabled,
  readOnly,
  submitAttempted = false,
}: Props) => {
  const { t } = useTranslation();
  const allocations = useFieldArray({ control, name: `targets.${index}.allocations` });

  const target = watch(`targets.${index}`);
  const headerName = `${target?.first_name ?? ''} ${target?.last_name ?? ''}`.trim();
  const pinfl = target?.pinfl ?? '';
  const packageName = target?.package_name ?? '';

  const regionOptions = REGION_VALUES.map((r) => ({ value: r, label: t(`regions.${r}`) }));
  const weightUnitOptions = WEIGHT_UNIT_VALUES.map((u) => ({
    value: u,
    label: t(`packages.form.weightUnit.${u}`),
  }));
  const productOptions = products.map((p) => ({ value: p.id, label: p.full_name }));

  // Auto-generate the package name exactly once per card as soon as the field is
  // empty (a fresh target). No user action — the name is always machine
  // assigned, mirroring the client split UI. The ref guard makes the fetch
  // fire-once: it survives the brief window where package_name is still '' before
  // the response lands, so the server counter is never double-incremented and
  // there is no regeneration loop. Existing targets (edit flow) arrive with a
  // name already set and are skipped, preserving their saved value.
  const autoNamedRef = useRef(false);
  useEffect(() => {
    if (autoNamedRef.current || packageName) return;
    autoNamedRef.current = true;
    void apiPackagesNextName().then(({ name }) => {
      setValue(`targets.${index}.package_name`, name, { shouldDirty: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageName]);

  // Per-package price (display only) = weight in kg × USD price per kg.
  const wRaw = Number(normalizeDecimal((target?.package_weight ?? '').toString()));
  const kg = target?.package_weight_unit === 'kg' ? wRaw : wRaw / 1000;
  // Mirrors backend save_distribution: an approved target whose weight/unit
  // is unchanged keeps its saved price; otherwise weight × current rate.
  const saved = target?.saved_package;
  const savedW = saved ? Number(normalizeDecimal(saved.weight)) : Number.NaN;
  // The editor sends a blank weight as 0; a legacy null saved weight never
  // matches (the backend then re-computes).
  const sameWeight =
    !!saved &&
    saved.weight.trim() !== '' &&
    saved.unit === target?.package_weight_unit &&
    savedW === (Number.isFinite(wRaw) ? wRaw : 0);
  const computedPrice = sameWeight && saved
    ? saved.price
    : roundMoney(Number.isFinite(wRaw) ? kg * pricePerKg : 0);

  // Per-PINFL customs limit (display only): used = baseline for the LIVE
  // PINFL excluding this parcel (server) + this card's allocations valued at
  // each product's unit price, skipping products the server does not count
  // (created before the usage window: last period / before an admin reset).
  // Equal is GREEN; strictly over is RED.
  const { data: limit } = usePinflLimit(pinfl, parcelId || undefined);
  const limitApplies = Boolean(limit?.limit_applies);
  const allocList = target?.allocations ?? [];
  const allocSum = allocList.reduce((sum, al) => {
    if (!isCountedInLimit(limit, al.product_id)) return sum;
    const prod = products.find((p) => p.id === al.product_id);
    return sum + (Number(al.quantity) || 0) * (prod ? Number(prod.price_per_item) || 0 : 0);
  }, 0);
  const used = roundMoney((limit?.used_amount ?? 0) + allocSum);
  const ceiling = limit?.limit ?? 0;
  const overLimit = used > ceiling;

  const addAllocation = () =>
    allocations.append({
      product_id: products.length === 1 ? (products[0]?.id ?? '') : '',
      quantity: 1,
    });

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-ink-muted" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
          )}
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-ink">
              {headerName || t('splits.save.recipient.title')}
            </span>
            <span className="block truncate font-mono text-xs text-ink-muted">
              {pinfl || '—'}
            </span>
          </span>
        </button>
        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={onRemove}
          >
            {t('splits.save.removePinfl')}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-slate-100 p-4">
          {/* Per-PINFL package — kept at the TOP of the card */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              label={t('splits.save.package.name')}
              required
              disabled
              {...register(`targets.${index}.package_name`)}
            />
            <NumberInput
              label={t('splits.save.package.priceOptional')}
              placeholder={computedPrice.toFixed(2)}
              hint={t('splits.save.package.priceHint')}
              disabled={readOnly}
              {...register(`targets.${index}.package_price`)}
            />
            <NumberInput
              label={t('splits.save.package.weight')}
              disabled={readOnly}
              {...register(`targets.${index}.package_weight`)}
            />
            <Select
              label={t('splits.save.package.weightUnit')}
              options={weightUnitOptions}
              disabled={readOnly}
              {...register(`targets.${index}.package_weight_unit`)}
            />
          </div>

          {!readOnly && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!packageName}
                leftIcon={<QrCode className="h-4 w-4" />}
                onClick={() => downloadQr(packageName, `${packageName}.png`)}
              >
                {t('splits.save.downloadQr')}
              </Button>
            </div>
          )}

          {/* Recipient / address */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              label={t('splits.save.recipient.pinfl')}
              required
              disabled={readOnly}
              error={
                submitAttempted && !pinfl.trim() ? t('splits.save.errors.pinflRequired') : undefined
              }
              {...register(`targets.${index}.pinfl`)}
            />
            <Input
              label={t('splits.save.recipient.firstName')}
              disabled={readOnly}
              {...register(`targets.${index}.first_name`)}
            />
            <Input
              label={t('splits.save.recipient.lastName')}
              disabled={readOnly}
              {...register(`targets.${index}.last_name`)}
            />
            <Input
              label={t('splits.save.recipient.middleName')}
              disabled={readOnly}
              {...register(`targets.${index}.middle_name`)}
            />
            <Input
              label={t('splits.save.recipient.phone')}
              disabled={readOnly}
              {...register(`targets.${index}.phone_number`)}
            />
            <Input
              label={t('splits.save.recipient.passport')}
              disabled={readOnly}
              {...register(`targets.${index}.passport_series`)}
            />
            <Input
              label={t('splits.save.recipient.addressName')}
              disabled={readOnly}
              {...register(`targets.${index}.address_name`)}
            />
            <Select
              label={t('splits.save.recipient.region')}
              options={regionOptions}
              disabled={readOnly}
              {...register(`targets.${index}.region`)}
            />
            <Input
              label={t('splits.save.recipient.city')}
              disabled={readOnly}
              {...register(`targets.${index}.city`)}
            />
            <Input
              label={t('splits.save.recipient.street')}
              disabled={readOnly}
              {...register(`targets.${index}.street`)}
            />
            <Input
              label={t('splits.save.recipient.building')}
              disabled={readOnly}
              {...register(`targets.${index}.building`)}
            />
            <Input
              label={t('splits.save.recipient.apartment')}
              disabled={readOnly}
              {...register(`targets.${index}.apartment`)}
            />
          </div>
          <Textarea
            label={t('splits.save.recipient.additionalInfo')}
            disabled={readOnly}
            {...register(`targets.${index}.additional_info`)}
          />

          {/* Allocations */}
          <div className="space-y-2">
            <p className="text-sm font-semibold text-ink">
              {t('splits.save.allocations.title')}
            </p>
            {allocations.fields.length === 0 && (
              <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-slate-200 py-6 text-ink-muted">
                <PackageOpen className="h-6 w-6" />
                <span className="text-sm">{t('splits.save.allocations.empty')}</span>
              </div>
            )}
            {allocations.fields.map((field, j) => {
              const al = allocList[j];
              const prod = products.find((p) => p.id === al?.product_id);
              const qty = Number(al?.quantity) || 0;
              const overAvail = prod ? qty > (Number(prod.quantity) || 0) : false;
              // Highlight RED when this product is over-allocated across ALL
              // PINFLs, or when this single allocation exceeds availability.
              const over = overAvail || (al?.product_id ? Boolean(productOverMap.get(al.product_id)) : false);
              const qtyInvalid = submitAttempted && allocQtyInvalid(al?.quantity);
              return (
                <div key={field.id} className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <div className="flex-1">
                    <Select
                      options={productOptions}
                      placeholder={t('splits.save.allocations.selectPlaceholder')}
                      disabled={readOnly}
                      {...register(`targets.${index}.allocations.${j}.product_id`)}
                    />
                  </div>
                  <div className="w-full sm:w-32">
                    <Input
                      type="number"
                      inputMode="numeric"
                      disabled={readOnly}
                      error={
                        qtyInvalid
                          ? t('parcels.products.errors.quantityInvalid')
                          : over
                            ? t('splits.save.errors.exceedsAvailable')
                            : undefined
                      }
                      {...register(`targets.${index}.allocations.${j}.quantity`)}
                    />
                  </div>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={disabled}
                      leftIcon={<Trash2 className="h-4 w-4" />}
                      onClick={() => allocations.remove(j)}
                    >
                      {t('splits.save.allocations.removeProduct')}
                    </Button>
                  )}
                </div>
              );
            })}
            {!readOnly && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={addAllocation}
              >
                {t('splits.save.allocations.addProduct')}
              </Button>
            )}
          </div>

          {limitApplies && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="text-sm text-ink-muted">{t('splits.save.limit.label')}</span>
              <span className={cn('text-sm font-semibold', overLimit ? 'text-red-600' : 'text-emerald-600')}>
                {t('splits.save.limit.value', {
                  used: formatUsd(used),
                  ceiling: formatUsd(ceiling),
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
